import { createContext, useContext, useEffect, useReducer } from "react";
import { useDataLayer } from "data-layer";
export class RoundNotFoundError extends Error {
  constructor(chainId, roundId) {
    super(`Round not found: chainId=${chainId}, roundId=${roundId}`);
  }
}
var ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["SET_LOADING"] = "SET_LOADING";
  ActionType["FINISH_LOADING"] = "FINISH_LOADING";
  ActionType["SET_ROUNDS"] = "SET_ROUNDS";
  ActionType["SET_ERROR_LIST_ROUNDS"] = "SET_ERROR_LIST_ROUNDS";
  ActionType["ADD_ROUND"] = "ADD_ROUND";
  ActionType["SET_ERROR_GET_ROUND"] = "SET_ERROR_GET_ROUND";
  ActionType["SET_ROUND_ID"] = "SET_ROUND_ID";
  return ActionType;
}(ActionType || {});
export const initialRoundState = {
  rounds: [],
  isLoading: true
};
export const RoundContext = /*#__PURE__*/createContext(undefined);
const roundReducer = (state, action) => {
  var _action$payload;
  switch (action.type) {
    case ActionType.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    case ActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false
      };
    case ActionType.SET_ROUNDS:
      return {
        ...state,
        rounds: (_action$payload = action.payload) !== null && _action$payload !== void 0 ? _action$payload : [],
        listRoundsError: undefined
      };
    case ActionType.SET_ERROR_LIST_ROUNDS:
      return {
        ...state,
        rounds: [],
        listRoundsError: action.payload
      };
    case ActionType.ADD_ROUND:
      return {
        ...state,
        rounds: state.rounds.concat(action.payload),
        getRoundByIdError: undefined
      };
    case ActionType.SET_ERROR_GET_ROUND:
      // log error to console to be sure we can debug it
      console.error(action.payload);
      return {
        ...state,
        getRoundByIdError: action.payload
      };
    case ActionType.SET_ROUND_ID:
      return {
        ...state,
        currentRoundId: action.payload
      };
    default:
      return state;
  }
};
export const RoundProvider = _ref => {
  let {
    children
  } = _ref;
  const [state, dispatch] = useReducer(roundReducer, initialRoundState);
  const providerProps = {
    state,
    dispatch
  };
  return /*#__PURE__*/_jsx(RoundContext.Provider, {
    value: providerProps,
    children: children
  });
};
function fetchRoundsById(dispatch, dataLayer, chainId, roundId) {
  dispatch({
    type: ActionType.SET_LOADING,
    payload: true
  });
  dataLayer.getRoundForExplorer({
    roundId,
    chainId
  }).then(result => {
    if (result === null) {
      dispatch({
        type: ActionType.SET_ERROR_GET_ROUND,
        payload: new RoundNotFoundError(chainId, roundId)
      });
    } else {
      const {
        round
      } = result;
      dispatch({
        type: ActionType.ADD_ROUND,
        payload: round
      });
    }
  }).catch(error => dispatch({
    type: ActionType.SET_ERROR_GET_ROUND,
    payload: error
  })).finally(() => dispatch({
    type: ActionType.FINISH_LOADING
  }));
}
export const useRoundById = (chainId, roundId) => {
  const context = useContext(RoundContext);
  if (context === undefined) {
    throw new Error("useRoundById must be used within a RoundProvider");
  }
  const dataLayer = useDataLayer();
  useEffect(() => {
    context.dispatch({
      type: ActionType.SET_ROUND_ID,
      payload: roundId
    });
    if (roundId) {
      const existingRound = context.state.rounds.find(round => round.id === roundId && round.chainId === chainId);
      if (!(existingRound !== null && existingRound !== void 0 && existingRound.token)) {
        fetchRoundsById(context.dispatch, dataLayer, chainId, roundId);
      }
    }
  }, [chainId, roundId]); // eslint-disable-line react-hooks/exhaustive-deps

  const round = context.state.rounds.find(round => round.id === roundId && round.chainId === chainId);
  return {
    round: round,
    isLoading: context.state.isLoading,
    getRoundByIdError: context.state.getRoundByIdError
  };
};
import { useQuery } from "@tanstack/react-query";
import { jsx as _jsx } from "react/jsx-runtime";
export const useRoundNamesByIds = rounds => {
  const dataLayer = useDataLayer();
  return useQuery({
    queryKey: ["roundNamesByIds", rounds],
    // Query key
    enabled: !!rounds && Object.keys(rounds).length > 0,
    // Only fetch if there are rounds
    queryFn: async () => {
      if (!rounds) {
        return {};
      }
      // Create an empty object to store chainId -> roundId -> roundName
      const roundNames = {};
      await Promise.all(Object.entries(rounds).map(async _ref2 => {
        var _await$dataLayer$getR, _fetchedRound$roundMe;
        let [chainId, roundId] = _ref2;
        // If the name is missing, fetch the round data
        const fetchedRound = (_await$dataLayer$getR = await dataLayer.getRoundForExplorer({
          roundId,
          chainId: Number(chainId)
        })) === null || _await$dataLayer$getR === void 0 ? void 0 : _await$dataLayer$getR.round;

        // Store the fetched round name in the result object
        if (fetchedRound !== null && fetchedRound !== void 0 && (_fetchedRound$roundMe = fetchedRound.roundMetadata) !== null && _fetchedRound$roundMe !== void 0 && _fetchedRound$roundMe.name) {
          if (!roundNames[Number(chainId)]) {
            roundNames[Number(chainId)] = {};
          }
          roundNames[Number(chainId)][roundId] = fetchedRound.roundMetadata.name;
        }
      }));
      return roundNames;
    }
  });
};